// Pure "what's the ONE next action?" derivation for the post-approval Sales
// Order detail page (OrderDetailPage → NextActionCta). Kept dependency-free
// (no React, no @/ imports) so the qa-orders-ux-rebuild gate can import and
// EXECUTE deriveNextAction() against fixtures — a source lint can't prove the
// state machine picks the right single action; running it can.
//
// CEO 2026-07-24: the four-step "What's next" strip was NOT intuitive — a
// horizontal progress rail asked the rep to read four columns and infer which
// one to touch. The correction: the flow itself makes the next step obvious via
// ONE prominent, state-aware primary action. This module collapses the old
// four-step state machine (Approved → Create POs → Send → Tracking) into the
// single action the rep should take right now:
//
//   no product lines  → nothing to produce yet (add lines)
//   no active POs      → CREATE purchase orders
//   some POs unsent    → REVIEW & SEND the first unsent PO
//   all sent, some     → ADD TRACKING on the first PO without it
//     untracked
//   all tracked        → production COMPLETE (no action)
//
// Data source is unchanged — the project's purchase-order list payload
// (/projects/:id/purchase-orders) the ProjectPosTab already loads. Tracking
// presence isn't in that payload, so delivery progress is read from each PO's
// internalStage (it advances to `shipped` when shipments are logged on the PO
// page) and the rep is deep-linked there to add it.

/** The single next step the rep should take on a post-approval Quote. */
export type NextActionKind =
  | "no_lines" // no product lines → nothing to produce yet
  | "create_pos" // post-approval, no ACTIVE POs → create purchase orders
  | "send_pos" // active POs exist, ≥1 unsent → review & send the first unsent
  | "add_tracking" // all sent, ≥1 without tracking → add tracking on the first
  | "complete"; // every active PO tracked/delivered → production complete

/** The order slice the derivation reads — the full OrderDetail is a superset. */
export interface DeriveOrderInput {
  /** POs are built from product lines; without any there is nothing to make. */
  hasProductLines: boolean;
}

/** The PO slice the derivation reads — a subset of the project PO-list row.
 *  `id` + `poNumber` drive the CTA's deep link + label to the target PO;
 *  vendor + leg fields drive the scoped-recreate action (E3). */
export interface DerivePoInput {
  id: string;
  poNumber: number;
  internalStage: string;
  sentAt: string | null;
  vendorId: string;
  vendorName: string;
  poType: string;
  /** decoration_service → the supplier vendor it ships FROM (the only_vendor_id
   *  target for a scoped re-create of the decoration leg). */
  shipFromVendorId: string | null;
}

/** A vendor group with a cancelled leg that has NO live equivalent — a scoped
 *  re-create (POST /orders/:id/pos with only_vendor_id) will replace just it. */
export interface RecreatableVendor {
  /** The only_vendor_id to pass — always the SUPPLIER vendor of the group. */
  vendorId: string;
  vendorName: string;
}

/** The single derived action + the counts the CTA renders as its helper line. */
export interface NextAction {
  kind: NextActionKind;
  /** Target PO for send_pos / add_tracking — the first unsent / first without
   *  tracking. Undefined for no_lines / create_pos / complete. */
  poId?: string;
  poNumber?: number;
  /** Active (non-cancelled) PO count = the "M" in "N of M". */
  totalCount: number;
  /** Active POs that have been sent to their supplier. */
  sentCount: number;
  /** Active POs that carry tracking (derived from stage). */
  trackedCount: number;
  /** Cancelled POs on this order — drives the cancelled-acknowledging copy on
   *  the create step (an all-cancelled order re-prompts Create, not Complete). */
  cancelledCount: number;
  /** Vendor groups with a cancelled leg and NO live equivalent (E3). Non-empty
   *  even in MIXED states (live siblings present) so the rep can re-create JUST
   *  the missing leg per vendor — the only path that reaches only_vendor_id. */
  recreatableVendors: RecreatableVendor[];
}

// A PO has left the rep's desk (reached the supplier) once it is past `draft`.
// `sentAt` is the explicit signal; internalStage advancing past draft is the
// fallback. Cancelled POs are excluded from the action math entirely (see
// deriveNextAction) rather than treated as "sent", so isPoSent describes only
// live POs — a cancelled PO must never read as sent or tracked.
const SENT_STAGES = new Set([
  "sent",
  "confirmed",
  "in_production",
  "shipped",
  "delivered",
  "billed",
  "closed",
]);

// Tracking presence is NOT carried in the project PO-list payload (no shipment
// count column). Rather than add an API, we read tracking progress from the
// PO's own internalStage — it advances to `shipped` when shipments are logged
// on PurchaseOrderDetailPage — and deep-link reps there to enter tracking.
// `cancelled` is deliberately absent: a killed PO never contributes tracking.
const TRACKED_STAGES = new Set(["shipped", "delivered", "billed", "closed"]);

/** True once the PO has been sent to its supplier. */
export function isPoSent(po: DerivePoInput): boolean {
  return po.sentAt !== null || SENT_STAGES.has(po.internalStage);
}

/** True once the PO carries tracking (derived from its stage — see note). */
export function isPoTracked(po: DerivePoInput): boolean {
  return TRACKED_STAGES.has(po.internalStage);
}

/**
 * Derive the ONE next action from live order + PO data.
 *
 * Cancelled POs never advance the chain. Excluding them (rather than counting
 * them as resolved) means an ALL-cancelled order with product lines re-prompts
 * `create_pos` — a fresh PO can still be minted — instead of falsely reading as
 * `complete`. In MIXED cases the live POs alone drive the action; cancelled POs
 * only surface in `cancelledCount` for the acknowledging copy.
 *
 * Order matters for the target PO: POs are sorted by poNumber so "the first
 * unsent / first untracked" is deterministic and reads as the lowest-numbered.
 */
export function deriveNextAction(
  order: DeriveOrderInput,
  pos: DerivePoInput[],
): NextAction {
  const activePos = pos
    .filter((p) => p.internalStage !== "cancelled")
    .slice()
    .sort((a, b) => a.poNumber - b.poNumber);
  const cancelledCount = pos.length - activePos.length;
  const totalCount = activePos.length;
  const sentCount = activePos.filter(isPoSent).length;
  const trackedCount = activePos.filter(isPoTracked).length;

  // E3 — which vendor groups have a CANCELLED leg with no LIVE equivalent, so a
  // scoped re-create (only_vendor_id = the SUPPLIER vendor) mints just that leg.
  // Supplier leg keys on vendorId; decoration leg keys on shipFromVendorId (the
  // supplier). Vendor name resolves from any product_supply row for that vendor,
  // else the cancelled row itself. Surfaced even when live siblings exist.
  const liveSupplierVendors = new Set(
    activePos.filter((p) => p.poType === "product_supply").map((p) => p.vendorId),
  );
  const liveDecoShipFrom = new Set(
    activePos
      .filter((p) => p.poType === "decoration_service" && p.shipFromVendorId)
      .map((p) => p.shipFromVendorId as string),
  );
  const supplierNameByVendor = new Map<string, string>();
  for (const p of pos) {
    if (p.poType === "product_supply") supplierNameByVendor.set(p.vendorId, p.vendorName);
  }
  const recreatableMap = new Map<string, string>();
  for (const p of pos) {
    if (p.internalStage !== "cancelled") continue;
    if (p.poType === "product_supply" && !liveSupplierVendors.has(p.vendorId)) {
      recreatableMap.set(p.vendorId, p.vendorName);
    } else if (
      p.poType === "decoration_service" &&
      p.shipFromVendorId &&
      !liveDecoShipFrom.has(p.shipFromVendorId)
    ) {
      recreatableMap.set(
        p.shipFromVendorId,
        supplierNameByVendor.get(p.shipFromVendorId) ?? p.vendorName,
      );
    }
  }
  const recreatableVendors: RecreatableVendor[] = [...recreatableMap].map(
    ([vendorId, vendorName]) => ({ vendorId, vendorName }),
  );

  const base = {
    totalCount,
    sentCount,
    trackedCount,
    cancelledCount,
    recreatableVendors,
  };

  // Nothing to produce yet — POs are built from product lines.
  if (!order.hasProductLines) {
    return { kind: "no_lines", ...base };
  }

  // No ACTIVE POs (none created, or every one cancelled) → create.
  if (totalCount === 0) {
    return { kind: "create_pos", ...base };
  }

  // Some PO still unsent → review & send the first (lowest-numbered) unsent.
  const firstUnsent = activePos.find((p) => !isPoSent(p));
  if (firstUnsent) {
    return {
      kind: "send_pos",
      poId: firstUnsent.id,
      poNumber: firstUnsent.poNumber,
      ...base,
    };
  }

  // All sent; tracking missing on some → add tracking on the first without it.
  const firstUntracked = activePos.find((p) => !isPoTracked(p));
  if (firstUntracked) {
    return {
      kind: "add_tracking",
      poId: firstUntracked.id,
      poNumber: firstUntracked.poNumber,
      ...base,
    };
  }

  // Every active PO tracked/delivered → production complete.
  return { kind: "complete", ...base };
}

/** Stages at which the next-action CTA is shown (cancelled/quote → no CTA). */
const CTA_STAGES = new Set(["committed", "in_production", "shipped"]);

/** Whether the post-approval next-action CTA should render for this stage. */
export function shouldShowNextAction(statusStage: string): boolean {
  return CTA_STAGES.has(statusStage);
}
